import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

// First-party tracking domains (track.theirdomain.com). Returns the domains +
// the CNAME target the tenant must add + whether the platform CF is wired.
export function useTrackingDomains() {
  return useQuery({
    queryKey: ['tracking-domains'],
    queryFn: async () => {
      const { data } = await api.get('/api/tracking-domains');
      return data || { domains: [], cname_target: null, platform_ready: false };
    },
    staleTime: 15_000,
    // While a subdomain is still verifying, look again every 30 seconds: the
    // server follows Cloudflare every 5 minutes, and the page should turn green
    // without anyone pressing Verify.
    refetchInterval: (q) => ((q?.state?.data?.domains || []).some((d) => d.status === 'verifying') ? 30_000 : false),
  });
}

function invalidate(qc) { qc.invalidateQueries({ queryKey: ['tracking-domains'] }); }

export function useAddTrackingDomain() {
  const qc = useQueryClient();
  return useMutation({
    // A hostname, or { hostname, record_type: 'a' | 'cname' }.
    mutationFn: async (v) => (await api.post('/api/tracking-domains', typeof v === 'string' ? { hostname: v } : v)).data,
    onSuccess: () => invalidate(qc),
  });
}

export function useVerifyTrackingDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.post(`/api/tracking-domains/${id}/verify`)).data,
    onSuccess: () => invalidate(qc),
  });
}

export function useDeleteTrackingDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.delete(`/api/tracking-domains/${id}`)).data,
    onSuccess: () => invalidate(qc),
  });
}

// ─── Is this workspace first-party, and working right now? ──────────────────
// The server's canon (api services/firstPartyState) - the same answer the
// snippets, the setup checklist and the platform console read. Ambient: the
// header chip fetches it on every screen, so it is cheap and polled slowly.
export function useFirstPartyStatus() {
  return useQuery({
    queryKey: ['tracking-domains', 'status'],
    queryFn: async () => (await api.get('/api/tracking-domains/status')).data,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    retry: false,
  });
}

// What staying on our domain costs, measured in the workspace's own data.
// Heavier (a 28-day read), so only fetched when something shows it.
export function useFirstPartyCost(enabled = true) {
  return useQuery({
    queryKey: ['tracking-domains', 'cost'],
    queryFn: async () => (await api.get('/api/tracking-domains/cost')).data,
    enabled,
    staleTime: 30 * 60_000,
    retry: false,
  });
}

// Pages that loaded a tag from our domain in the last 7 days.
export function useOldTagPages(enabled = true) {
  return useQuery({
    queryKey: ['tracking-domains', 'old-tag-pages'],
    queryFn: async () => (await api.get('/api/tracking-domains/old-tag-pages')).data,
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });
}
